import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

interface UploadFile {
  id: string;
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error' | 'paused' | 'exists';
  error?: string;
  url?: string;
  recordingTimestamp?: Date;
  mappedActivity?: any;
  abortController?: AbortController;
  existsInAzure?: boolean;
  overwrite?: boolean;
  remoteBlobPath?: string;
  videoId?: string;
  audioId?: string;
}

interface ActivityFileSelection {
  activityId: string;
  videoFiles: string[]; // Array of file names
  audioFiles: string[]; // Array of file names
}

interface UploadContextType {
  uploadFiles: UploadFile[];
  setUploadFiles: React.Dispatch<React.SetStateAction<UploadFile[]>>;
  addUploadFile: (file: UploadFile) => void;
  updateUploadFile: (id: string, updates: Partial<UploadFile>) => void;
  removeUploadFile: (id: string) => void;
  clearCompletedUploads: () => void;
  getUploadStats: () => {
    total: number;
    pending: number;
    uploading: number;
    completed: number;
    error: number;
    paused: number;
    exists: number;
  };
  isUploading: boolean;
  // Session persistence
  homeId: string;
  setHomeId: (homeId: string) => void;
  selectedParticipant: string;
  setSelectedParticipant: (participant: string) => void;
  // File selection persistence
  activityFileSelections: ActivityFileSelection[];
  setActivityFileSelections: React.Dispatch<React.SetStateAction<ActivityFileSelection[]>>;
  updateActivityFileSelection: (activityId: string, type: 'video' | 'audio', files: string[]) => void;
  // Upload management
  retryStuckUploads: () => void;
}

const UploadContext = createContext<UploadContextType | undefined>(undefined);

export const useUpload = () => {
  const context = useContext(UploadContext);
  if (!context) {
    console.error('useUpload must be used within an UploadProvider');
    // Return a default context to prevent crashes during development
    return {
      uploadFiles: [],
      setUploadFiles: () => {},
      addUploadFile: () => {},
      updateUploadFile: () => {},
      removeUploadFile: () => {},
      clearCompletedUploads: () => {},
      getUploadStats: () => ({
        total: 0,
        pending: 0,
        uploading: 0,
        completed: 0,
        error: 0,
        paused: 0,
        exists: 0,
      }),
      isUploading: false,
      homeId: '',
      setHomeId: () => {},
      selectedParticipant: '',
      setSelectedParticipant: () => {},
      activityFileSelections: [],
      setActivityFileSelections: () => {},
      updateActivityFileSelection: () => {},
      retryStuckUploads: () => {},
    };
  }
  return context;
};

// Re-evaluate an uploading file against the stuck-upload timers (pure transform).
const evaluateStuckUpload = (
  file: UploadFile,
  now: number,
  uploadTimestamps: Record<string, number>,
  lastProgressTimestamps: Record<string, number>,
): UploadFile => {
  if (file.status !== 'uploading') return file;

  const lastProgressTime = lastProgressTimestamps[file.id] || uploadTimestamps[file.id] || now;
  const timeSinceLastProgress = now - lastProgressTime;

  // If upload has been stuck at 100% for more than 10 seconds, mark as completed
  if (file.progress >= 100 && timeSinceLastProgress > 10000) {
    return {
      ...file,
      status: 'completed',
      progress: 100,
      abortController: undefined,
    };
  }

  // Only timeout if NO PROGRESS for 5 minutes (not total upload time).
  // This allows large files to upload as long as progress is being made.
  if (file.progress > 0 && file.progress < 100 && timeSinceLastProgress > 300000) {
    return {
      ...file,
      status: 'error',
      error: 'Upload stalled - no progress for 5 minutes. Please check your network and retry',
      abortController: undefined,
    };
  }

  return file;
};

export const UploadProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);
  const [homeId, setHomeId] = useState<string>('');
  const [selectedParticipant, setSelectedParticipant] = useState<string>('');
  const [activityFileSelections, setActivityFileSelections] = useState<ActivityFileSelection[]>([]);
  const [previousHomeId, setPreviousHomeId] = useState<string>('');
  const [uploadTimestamps, setUploadTimestamps] = useState<Record<string, number>>({});
  const [lastProgressTimestamps, setLastProgressTimestamps] = useState<Record<string, number>>({});

  // Load upload state from localStorage on mount
  useEffect(() => {
    const savedUploads = localStorage.getItem('uploadFiles');
    if (savedUploads) {
      try {
        const parsed = JSON.parse(savedUploads);
        // Convert back to proper format (File objects can't be serialized, so we'll handle that)
        setUploadFiles(parsed.map((file: any) => ({
          ...file,
          // A transfer never outlives the page that ran it, so anything saved
          // mid-upload is interrupted by now. Restoring it as 'uploading'
          // would count it as in progress forever.
          ...(file.status === 'uploading'
            ? {status: 'error', error: 'Interrupted when the page was reloaded'}
            : {}),
          file: new File([], file.fileName || 'unknown'), // Create placeholder File object
          recordingTimestamp: file.recordingTimestamp ? new Date(file.recordingTimestamp) : undefined,
        })));
      } catch (error) {
        console.error('Error loading upload state from localStorage:', error);
      }
    }

    // Load session data from localStorage
    const savedHomeId = localStorage.getItem('uploadSession_homeId');
    const savedParticipant = localStorage.getItem('uploadSession_selectedParticipant');
    const savedFileSelections = localStorage.getItem('uploadSession_activityFileSelections');
    
    if (savedHomeId) {
      setHomeId(savedHomeId);
      setPreviousHomeId(savedHomeId);
    }
    if (savedParticipant) {
      setSelectedParticipant(savedParticipant);
    }
    if (savedFileSelections) {
      try {
        const parsed = JSON.parse(savedFileSelections);
        setActivityFileSelections(parsed);
      } catch (error) {
        console.error('Error loading file selections from localStorage:', error);
      }
    }
  }, []);

  // Save upload state to localStorage whenever it changes
  useEffect(() => {
    const serializableUploads = uploadFiles.map(file => ({
      ...file,
      fileName: file.file.name,
      file: undefined, // Remove File object as it can't be serialized
      abortController: undefined, // Remove AbortController as it can't be serialized
    }));
    localStorage.setItem('uploadFiles', JSON.stringify(serializableUploads));
  }, [uploadFiles]);

  // Save session data to localStorage whenever it changes
  useEffect(() => {
    if (homeId) {
      localStorage.setItem('uploadSession_homeId', homeId);
    } else {
      localStorage.removeItem('uploadSession_homeId');
    }
  }, [homeId]);

  // Clear uploads and file selections when homeId changes
  useEffect(() => {
    // Only clear if homeId has actually changed and is not empty
    if (homeId && previousHomeId && homeId !== previousHomeId) {
      // Abort any ongoing uploads before clearing
      uploadFiles.forEach(file => {
        if (file.abortController && file.status === 'uploading') {
          file.abortController.abort();
        }
      });
      // Clear upload files
      setUploadFiles([]);
      // Clear file selections
      setActivityFileSelections([]);
      // Clear selected participant
      setSelectedParticipant('');
    }
    // Update previous homeId
    if (homeId) {
      setPreviousHomeId(homeId);
    }
  }, [homeId, previousHomeId, uploadFiles]);

  useEffect(() => {
    if (selectedParticipant) {
      localStorage.setItem('uploadSession_selectedParticipant', selectedParticipant);
    } else {
      localStorage.removeItem('uploadSession_selectedParticipant');
    }
  }, [selectedParticipant]);

  useEffect(() => {
    if (activityFileSelections.length > 0) {
      localStorage.setItem('uploadSession_activityFileSelections', JSON.stringify(activityFileSelections));
    } else {
      localStorage.removeItem('uploadSession_activityFileSelections');
    }
  }, [activityFileSelections]);

  // Monitor for stuck uploads (progress not updating)
  const checkStuckUploads = useCallback(() => {
    const now = Date.now();
    setUploadFiles(prev =>
      prev.map(file => evaluateStuckUpload(file, now, uploadTimestamps, lastProgressTimestamps))
    );
  }, [uploadTimestamps, lastProgressTimestamps]);

  useEffect(() => {
    const interval = setInterval(checkStuckUploads, 5000); // Check every 5 seconds
    return () => clearInterval(interval);
  }, [checkStuckUploads]);

  const addUploadFile = useCallback((file: UploadFile) => {
    setUploadFiles(prev => [...prev, file]);
  }, []);

  // Track upload start / progress / completion timestamps based on a status change.
  const trackUploadTimestamps = useCallback(
    (id: string, prevStatus: UploadFile['status'], prevProgress: number, updates: Partial<UploadFile>) => {
      // Track upload start time
      if (updates.status === 'uploading' && prevStatus !== 'uploading') {
        setUploadTimestamps(prev => ({ ...prev, [id]: Date.now() }));
        setLastProgressTimestamps(prev => ({ ...prev, [id]: Date.now() }));
      }

      // Track progress updates - update timestamp whenever progress changes
      if (updates.progress !== undefined && updates.progress !== prevProgress) {
        setLastProgressTimestamps(prev => ({ ...prev, [id]: Date.now() }));
      }

      // Clear timestamps when upload completes or errors
      if ((updates.status === 'completed' || updates.status === 'error') && prevStatus === 'uploading') {
        setUploadTimestamps(prev => {
          const newTimestamps = { ...prev };
          delete newTimestamps[id];
          return newTimestamps;
        });
        setLastProgressTimestamps(prev => {
          const newTimestamps = { ...prev };
          delete newTimestamps[id];
          return newTimestamps;
        });
      }
    },
    []
  );

  const updateUploadFile = useCallback((id: string, updates: Partial<UploadFile>) => {
    setUploadFiles(prev => {
      const current = prev.find(file => file.id === id);
      if (current) {
        trackUploadTimestamps(id, current.status, current.progress, updates);
      }
      return prev.map(file => (file.id === id ? { ...file, ...updates } : file));
    });
  }, [trackUploadTimestamps]);

  const removeUploadFile = useCallback((id: string) => {
    setUploadFiles(prev => prev.filter(file => file.id !== id));
  }, []);

  const clearCompletedUploads = useCallback(() => {
    setUploadFiles(prev => prev.filter(file => 
      file.status !== 'completed' && file.status !== 'error'
    ));
  }, []);

  const updateActivityFileSelection = useCallback((activityId: string, type: 'video' | 'audio', files: string[]) => {
    setActivityFileSelections(prev => {
      const existing = prev.find(selection => selection.activityId === activityId);
      if (existing) {
        return prev.map(selection => 
          selection.activityId === activityId 
            ? { ...selection, [type === 'video' ? 'videoFiles' : 'audioFiles']: files }
            : selection
        );
      } else {
        return [...prev, {
          activityId,
          videoFiles: type === 'video' ? files : [],
          audioFiles: type === 'audio' ? files : [],
        }];
      }
    });
  }, []);

  const retryStuckUploads = useCallback(() => {
    setUploadFiles(prev => 
      prev.map(file => {
        // Check if upload has been stuck for more than 30 seconds
        if (file.status === 'uploading' && file.progress > 0 && file.progress < 100) {
          // Reset to pending status to allow retry
          return {
            ...file,
            status: 'pending' as const,
            error: 'Upload was stuck, reset to pending',
            abortController: undefined,
          };
        }
        return file;
      })
    );
  }, []);

  const getUploadStats = useCallback(() => {
    return {
      total: uploadFiles.length,
      pending: uploadFiles.filter(f => f.status === 'pending').length,
      uploading: uploadFiles.filter(f => f.status === 'uploading').length,
      completed: uploadFiles.filter(f => f.status === 'completed').length,
      error: uploadFiles.filter(f => f.status === 'error').length,
      paused: uploadFiles.filter(f => f.status === 'paused').length,
      exists: uploadFiles.filter(f => f.status === 'exists').length,
    };
  }, [uploadFiles]);

  const isUploading = uploadFiles.some(f => f.status === 'uploading');

  const value: UploadContextType = {
    uploadFiles,
    setUploadFiles,
    addUploadFile,
    updateUploadFile,
    removeUploadFile,
    clearCompletedUploads,
    getUploadStats,
    isUploading,
    // Session persistence
    homeId,
    setHomeId,
    selectedParticipant,
    setSelectedParticipant,
    // File selection persistence
    activityFileSelections,
    setActivityFileSelections,
    updateActivityFileSelection,
    // Upload management
    retryStuckUploads,
  };

  return (
    <UploadContext.Provider value={value}>
      {children}
    </UploadContext.Provider>
  );
};
